import { API, graphqlOperation } from 'aws-amplify';

import { type QueryResult } from '../../../Admin/Datasources/QueryFactory';
import { type CreateExternalDocumentInput } from '../../../API';
import { externalDocumentFromDb, externalDocumentItemFields } from '../../ExternalDocuments/ExternalDocuments';
import { getOpenFlightsForUser } from '../../Flights/FlightsDatasource';
import { type LocalDB } from '../../LocalDB/LocalDB';
import { type DbItems, type MaybeNextToken } from '../../LocalDB/types';
import { type FlightSupplementalExternalDocumentData } from '../FlightSupplementalDataTypes';
import { FlightSupplementalDataType } from '../FlightSupplementalDataTypes';
import { itemIsUseful } from './Cleanup';

export async function loadExternalDocuments(db: LocalDB, userId: string) {
    const openFlights = await getOpenFlightsForUser(userId);
    const openFlightIds = openFlights.map((f) => f.id);
    const openReservationIds = openFlights.map((f) => f.reservationId).filter(Boolean) as string[];
    const variables: { [key: string]: string } = {};
    const variableNames: string[] = [];
    const subqueries: string[] = [];
    const aliases = new Map<string, SubqueryKind & { id: string }>();
    const addSubqueries = (kind: SubqueryKind, ids: string[]) => {
        for (const id of ids) {
            const index = subqueries.length;
            const variableName = `${kind.variableKind}${index}`;
            variableNames.push(variableName);
            variables[variableName] = id;
            aliases.set(`${kind.queryCommand}${index}`, { ...kind, id });
            subqueries.push(listExternalDocumentsBySomeIdId(kind.queryCommand, kind.variableKind, index));
        }
    };
    addSubqueries({ queryCommand: 'listExternalDocumentsByFlightId', variableKind: 'flightId' }, openFlightIds);
    addSubqueries(
        { queryCommand: 'listExternalDocumentsByReservationId', variableKind: 'reservationId' },
        openReservationIds
    );
    const variableDefinitionString = variableNames.map((v) => `$${v}: ID!`).join(', ');
    const query = `
    query supplementalExternalDocuments${variableDefinitionString.length ? `(${variableDefinitionString})` : ''} {
        ${subqueries.join('\n')}
        listExternalDocumentsByNotIdentified(limit: 50, sortDirection: DESC, notIdentified: "x") {
            items {
                ${externalDocumentItemFields}
            }
        }
    }
    `;
    const result = (await API.graphql(graphqlOperation(query, variables))) as QueryResult<{
        [key: string]: DbItems<CreateExternalDocumentInput> & MaybeNextToken;
    }>;
    if (result.data) {
        const map = new Map<string, FlightSupplementalExternalDocumentData>();
        const addItems = (items: ExternalDocumentItems) => {
            items.forEach((i) => {
                const document = externalDocumentFromDb(i);
                const blockOffTimestamp = parseInt(document.identifyingData?.blockOffTimestamp || '0', 10);
                if (!blockOffTimestamp) {
                    return;
                }
                const externalDocumentData: FlightSupplementalExternalDocumentData = {
                    id: document.id,
                    flightId: document.flightId || undefined,
                    legId: document.legId || undefined,
                    reservationId: document.reservationId || undefined,
                    reservationEventId: document.reservationEventId || undefined,
                    usefulnessEndsByDate: new Date((blockOffTimestamp + 14 * 86400) * 1e3),
                    type: FlightSupplementalDataType.externalDocumentData,
                    externalDocument: document,
                };
                map.set(document.id, externalDocumentData);
            });
        };
        for (const [alias, list] of Object.entries(result.data)) {
            addItems(list.items);
            const subquery = aliases.get(alias);
            if (subquery && list.nextToken) {
                addItems(await loadRemainingPages(subquery, list.nextToken));
            }
        }
        if (map.size) {
            const itemsToPut = [...map.values()].filter(itemIsUseful);
            await db.flightSupplementalData.bulkPut(itemsToPut);
        }
    }
}

type ExternalDocumentItems = DbItems<CreateExternalDocumentInput>['items'];
type SubqueryKind = { queryCommand: string; variableKind: string };

async function loadRemainingPages(
    { queryCommand, variableKind, id }: SubqueryKind & { id: string },
    firstNextToken: string
): Promise<ExternalDocumentItems> {
    const query = `
    query supplementalExternalDocumentsPage($${variableKind}: ID!, $nextToken: String) {
        list: ${queryCommand}(${variableKind}: $${variableKind}, limit: 100, sortDirection: DESC, nextToken: $nextToken) {
            items {
                ${externalDocumentItemFields}
            }
            nextToken
        }
    }`;
    const items: ExternalDocumentItems = [];
    let nextToken: string | null | undefined = firstNextToken;
    while (nextToken) {
        const result = (await API.graphql(graphqlOperation(query, { [variableKind]: id, nextToken }))) as QueryResult<{
            list: DbItems<CreateExternalDocumentInput> & MaybeNextToken;
        }>;
        items.push(...result.data.list.items);
        nextToken = result.data.list.nextToken;
    }

    return items;
}

const listExternalDocumentsBySomeIdId = (queryCommand: string, variableName: string, index: number) => {
    return `
    ${queryCommand}${index}: ${queryCommand}(${variableName}: $${variableName}${index}, limit: 40, sortDirection: DESC) {
        items {
            ${externalDocumentItemFields}
        }
        nextToken
    }`;
};
